import { z } from 'zod';
import { EmailAddressSchema } from './auth-settings.types.js';

/**
 * Types for the first-party email + password auth plugin (`auth-email`).
 *
 * Email verification is intentionally out of scope for now: registration
 * creates an immediately-usable account. The schemas live in shared so the
 * plugin server routes, the host setup wizard, and the web client all agree
 * on the request shapes.
 */

/**
 * Password rules for setting/changing a credential. Deliberately modest
 * (8+ characters) so the onboarding default ("password") is accepted while
 * still blocking trivially short values. Login does not re-validate length —
 * an existing credential that predates a stricter rule must still resolve.
 */
export const EmailAuthPasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(200, 'Password is too long');

export const EmailLoginRequestSchema = z.object({
  email: EmailAddressSchema,
  password: z.string().min(1, 'Password is required'),
});
export type EmailLoginRequest = z.infer<typeof EmailLoginRequestSchema>;

export const EmailRegisterRequestSchema = z.object({
  email: EmailAddressSchema,
  password: EmailAuthPasswordSchema,
  /** Optional display name; falls back to the email local-part when omitted. */
  name: z.string().trim().min(1).max(120).optional(),
});
export type EmailRegisterRequest = z.infer<typeof EmailRegisterRequestSchema>;

/**
 * Body for `POST /setup/admin-account` — the one-time onboarding step that
 * creates the first email/password administrator and promotes them to
 * super-admin. Same shape as registration; only reachable while the setup
 * gate is open.
 */
export const SetupAdminAccountRequestSchema = EmailRegisterRequestSchema;
export type SetupAdminAccountRequest = z.infer<typeof SetupAdminAccountRequestSchema>;

/**
 * Response from `GET /auth/email/registration` — whether self-service email
 * registration is currently allowed. Driven by the plugin's service-level
 * configuration (`registrationEnabled`).
 */
export const EmailRegistrationStatusSchema = z.object({
  enabled: z.boolean(),
});
export type EmailRegistrationStatus = z.infer<typeof EmailRegistrationStatusSchema>;

/**
 * Query for `GET /auth/email/verify` and body for `POST /auth/email/verify`:
 * the plaintext token from the verification email.
 */
export const EmailVerifyTokenSchema = z.object({
  token: z.string().min(1, 'Verification token is required'),
});
export type EmailVerifyToken = z.infer<typeof EmailVerifyTokenSchema>;

/** Response from `POST /auth/email/verify/request`. */
export const EmailVerificationRequestStatusSchema = z.object({
  status: z.enum(['sent', 'already_verified', 'no_password_identity']),
});
export type EmailVerificationRequestStatus = z.infer<typeof EmailVerificationRequestStatusSchema>;

/**
 * Where the verify link lands after the server consumes it. The GET handler
 * redirects to `<APP_URL>/settings` with one of these query markers so the
 * Settings page can show the outcome; the page clears them after reading.
 */
export const EMAIL_VERIFICATION_LANDING_PATH = '/settings';
export const EMAIL_VERIFIED_QUERY_PARAM = 'email_verified';
export const EMAIL_VERIFY_FAILED_AUTH_ERROR = 'email_verify_failed';
